/**
 * Audio & difficulty configuration helpers.
 *
 * Provides functions to compute effective volumes and difficulty multipliers
 * based on the persisted config settings. All scenes and entities should use
 * these helpers instead of hardcoding volume values.
 *
 * Usage:
 *   import { getMusicVol, getSfxVol, applyMasterMute, getDifficultyMultiplier } from '../utils/audio-config.js';
 *   const vol = getMusicVol(0.4);  // base 0.4 scaled by user's music volume setting
 */

import { VOLUME_STEPS, DIFFICULTY_DRAIN_MULTIPLIER, DEFAULT_DIFFICULTY } from '../config/game-config.js';
import { loadConfig } from './save-manager.js';

/** @type {import('./save-manager.js').ConfigSettings|null} Cached config (refreshed on demand). */
let _cachedConfig = null;

/**
 * Refresh the cached config from localStorage.
 * Call this when the config scene saves new settings, or at scene start.
 */
export function refreshConfig() {
  _cachedConfig = loadConfig();
}

/**
 * Get the current config (loads from cache or localStorage).
 * @returns {import('./save-manager.js').ConfigSettings}
 */
export function getConfig() {
  if (!_cachedConfig) {
    _cachedConfig = loadConfig();
  }
  return _cachedConfig;
}

/**
 * Compute the effective music volume for a given base volume.
 *
 * @param {number} baseVolume - The original/intended volume (e.g. 0.4).
 * @returns {number} Scaled volume (0.0 – baseVolume).
 */
export function getMusicVol(baseVolume) {
  if (typeof baseVolume !== 'number' || isNaN(baseVolume)) return 0;
  const cfg = getConfig();
  if (cfg.masterMute) return 0;
  const multiplier = VOLUME_STEPS[cfg.musicVolumeStep] ?? 0.5;
  return baseVolume * multiplier;
}

/**
 * Compute the effective SFX volume for a given base volume.
 *
 * @param {number} baseVolume - The original/intended volume (e.g. 0.5).
 * @returns {number} Scaled volume (0.0 – baseVolume).
 */
export function getSfxVol(baseVolume) {
  if (typeof baseVolume !== 'number' || isNaN(baseVolume)) return 0;
  const cfg = getConfig();
  if (cfg.masterMute) return 0;
  const multiplier = VOLUME_STEPS[cfg.sfxVolumeStep] ?? 0.75;
  return baseVolume * multiplier;
}

/**
 * Apply master mute state to the Phaser sound manager.
 * Should be called at scene creation to sync the mute toggle.
 *
 * @param {Phaser.Sound.BaseSoundManager} soundManager
 */
export function applyMasterMute(soundManager) {
  const cfg = getConfig();
  soundManager.mute = cfg.masterMute;
}

/**
 * Get the health drain multiplier for the current difficulty.
 * Applied to ALL health drains (base move, hazard multipliers, idle drain, poison/spike ticks).
 *
 * @returns {number} 0.4 (easy), 0.75 (normal), or 1.0 (hard).
 */
export function getDifficultyMultiplier() {
  const cfg = getConfig();
  return DIFFICULTY_DRAIN_MULTIPLIER[cfg.difficulty] ?? 1.0;
}

/**
 * Get the current difficulty setting string.
 *
 * @returns {string} 'easy', 'normal', or 'hard'.
 */
export function getDifficulty() {
  const cfg = getConfig();
  return cfg.difficulty || DEFAULT_DIFFICULTY;
}
